import { when as rWhen, isDefined as rIsDefined, constant as rConstant } from 'remeda';

import { type CampaignRequestStatusEnum, type operations } from '@/libs/openapi';
import { fromApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import type { CampaignRequestProgramListQueryFnResult } from '@/resources/campaign-request-program/campaign-request-program.api';
import type { OrganizationTeamQueryFnResult } from '@/resources/organization-team/organization-team.api';
import type { OrganizationUserListQueryFnResult } from '@/resources/organization-user/organization-user.api';
import { fromApiUserFieldAdapter } from '@/resources/user/user.field-adapter';
import { defineFilterListAdapter } from '@/utils/define-filter-list-adapter.util';
import { formatDate, formatNumber } from '@/utils/format.util';

type CampaignRequestFilters = Required<Pick<NonNullable<operations['CampaignRequestController_getMany']['parameters']['query']>, 'totalBudgetGte' | 'totalBudgetLte' | 'creativeType' | 'targetingType' | 'teamId' | 'hasNewComments' | 'hasPendingEdits' | 'startDateGte' | 'endDateLte'>> & {
  id: number;
  status: CampaignRequestStatusEnum[];
  ownerId: string;
  programId: number;
};

interface CampaignRequestFilterCtx {
  owner: OrganizationUserListQueryFnResult['items'][number] | undefined;
  team: OrganizationTeamQueryFnResult['items'][number] | undefined;
  program?: CampaignRequestProgramListQueryFnResult['data'][number] | undefined;
  isOwnerPending?: boolean;
  isTeamPending?: boolean;
  isProgramPending?: boolean;
}

export const campaignFilterListAdapter = defineFilterListAdapter<CampaignRequestFilters, CampaignRequestFilterCtx>({
  id: (value) => ({
    category: 'ID',
    value: value.toString(),
  }),

  status: (value) => value.map((status) => ({
    filterValue: status,
    category: 'Status',
    value: fromApiCampaignRequestFieldAdapter.forLabel.status(status),
  })),

  totalBudgetGte: (value) => ({
    category: 'Budget',
    value: `From ${formatNumber(value, 'currency')} USD`,
  }),

  totalBudgetLte: (value) => ({
    category: 'Budget',
    value: `To ${formatNumber(value, 'currency')} USD`,
  }),

  creativeType: (value) => ({
    category: 'Content type',
    value: fromApiCampaignRequestFieldAdapter.forLabel.creativeType(value), // The value comes from the ui, but uses API values, so we need to convert them as if they came from the API
  }),

  targetingType: (value) => ({
    category: 'Targeting type',
    value: fromApiCampaignRequestFieldAdapter.forLabel.targetingType(value),
  }),

  ownerId: (value, ctx) => ({
    category: 'Owner',
    isPending: ctx.isOwnerPending,
    value: rWhen(ctx.owner, rIsDefined<OrganizationUserListQueryFnResult['items'][number]>, {
      onTrue: fromApiUserFieldAdapter.getFullName,
      onFalse: rConstant(`#${value}`),
    }),
  }),

  teamId: (value, ctx) => ({
    category: 'Team',
    isPending: ctx.isTeamPending,
    value: rWhen(ctx.team, rIsDefined<OrganizationTeamQueryFnResult['items'][number]>, {
      onTrue: (t) => t.name,
      onFalse: rConstant(`#${value}`),
    }),
  }),

  programId: (value, ctx) => ({
    category: 'Program',
    isPending: ctx.isProgramPending,
    value: rWhen(ctx.program, rIsDefined<CampaignRequestProgramListQueryFnResult['data'][number]>, {
      onTrue: (p) => p.name,
      onFalse: rConstant(`#${value}`),
    }),
  }),

  hasNewComments: (value) => ({
    category: 'New comments',
    value: value ? 'Yes' : 'No',
  }),

  hasPendingEdits: (value) => ({
    category: 'Pending edit requests',
    value: value ? 'Yes' : 'No',
  }),

  startDateGte: (value) => ({
    category: 'Start date',
    value: formatDate('full')(value),
  }),

  endDateLte: (value) => ({
    category: 'End date',
    value: formatDate('full')(value),
  }),
});
